interface DebounceWaiter<T> {
  resolve: (value: T | PromiseLike<T>) => void
  reject: (reason?: unknown) => void
}

interface DebounceEntry<T> {
  timer: ReturnType<typeof setTimeout> | null
  latestOperation: () => Promise<T>
  waiters: DebounceWaiter<T>[]
}

export interface CalculatorDebounce {
  debounced: <T>(key: string, operation: () => Promise<T>) => Promise<T>
  flush: (key?: string) => void
}

export function createCalculatorDebounce(delayMs = 400): CalculatorDebounce {
  const entries = new Map<string, DebounceEntry<unknown>>()

  const clearEntry = (key: string): void => {
    const entry = entries.get(key)
    if (!entry) {
      return
    }

    if (entry.timer) {
      clearTimeout(entry.timer)
    }

    entry.waiters.forEach((waiter) => {
      waiter.reject(new Error('Calculator request canceled'))
    })
    entries.delete(key)
  }

  const debounced = <T>(key: string, operation: () => Promise<T>): Promise<T> => {
    const existing = entries.get(key) as DebounceEntry<T> | undefined
    const entry: DebounceEntry<T> = existing ?? {
      timer: null,
      latestOperation: operation,
      waiters: [],
    }

    entry.latestOperation = operation

    return new Promise<T>((resolve, reject) => {
      entry.waiters.push({ resolve, reject })

      if (entry.timer) {
        clearTimeout(entry.timer)
      }

      entry.timer = setTimeout(async () => {
        const waiters = [...entry.waiters]
        entries.delete(key)

        try {
          const result = await entry.latestOperation()
          waiters.forEach((waiter) => waiter.resolve(result))
        } catch (error) {
          waiters.forEach((waiter) => waiter.reject(error))
        }
      }, delayMs)

      entries.set(key, entry as DebounceEntry<unknown>)
    })
  }

  const flush = (key?: string): void => {
    if (key) {
      clearEntry(key)
      return
    }

    const keys = [...entries.keys()]
    keys.forEach((entryKey) => clearEntry(entryKey))
  }

  return {
    debounced,
    flush,
  }
}
